package com.arena_web.ui.components.forms

import androidx.compose.runtime.*
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement

private const val DIGIT_COLOR = "#bed6f5"
private const val DIGIT_FADED = "rgba(190, 214, 245, 0.22)"
private const val PAIR_BG = "var(--arena-surface-variant)"

@Composable
fun TimePicker(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    required: Boolean = false,
    @Suppress("UNUSED_PARAMETER") placeholder: String = "00 : 00",
    error: String? = null
) {
    // Local digits state — seeded once from incoming value. Subsequent external value
    // changes are intentionally NOT mirrored so partial typing isn't wiped.
    var localDigits by remember { mutableStateOf(value.filter { it.isDigit() }.take(4)) }
    var focused by remember { mutableStateOf(false) }
    var caretOn by remember { mutableStateOf(true) }

    // Blinking caret runs continuously while focused (even when all 4 digits are filled —
    // caret then parks at the end of the last slot, matching native input behaviour).
    LaunchedEffect(focused, localDigits.length) {
        caretOn = true
        if (!focused) return@LaunchedEffect
        while (true) {
            delay(530)
            caretOn = !caretOn
        }
    }

    // Slot index where the next digit would land (0..3). When length == 4 we park at slot 3
    // but render the caret on the trailing edge instead of overlapping the digit.
    val len = localDigits.length
    val caretSlot = if (focused && caretOn) len.coerceAtMost(3) else -1
    val caretAtEnd = focused && caretOn && len == 4

    Column(modifier = modifier.sGap(6), horizontalAlignment = Alignment.CenterHorizontally) {
        label?.let {
            Row(
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    text = it,
                    modifier = Modifier
                        .sFontSize(14)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                if (required) {
                    SpanText(
                        text = " *",
                        modifier = Modifier
                            .sFontSize(14)
                            .color(AppColors.Primary)
                    )
                }
            }
        }

        // Outer pill — project gradient border, subtle Arena-blue gradient overlay, 8px padding, rounded 18.
        Box(
            modifier = Modifier
                .sBorderRadius(18)
                .styleModifier {
                    property(
                        "border",
                        if (error != null) "0.3px solid ${AppColors.Error}"
                        else "0.3px solid transparent"
                    )
                    property(
                        "background",
                        "linear-gradient(15.2deg, rgba(0, 59, 174, 0.03) 26.9%, rgba(3, 87, 230, 0.03) 67.5%) padding-box, " +
                            "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                    property("padding", s(8))
                    property("box-sizing", "border-box")
                    property("position", "relative")
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("width", "fit-content")
                    property("cursor", "text")
                },
            contentAlignment = Alignment.Center
        ) {
            // Inner block — Figma's extra 8px padding around the digit row.
            Box(modifier = Modifier.sPadding(8).sBorderRadius(12)) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier
                        .sGap(6)
                        .styleModifier { property("pointer-events", "none") }
                ) {
                    DigitPairBox(
                        d0 = localDigits.getOrNull(0)?.toString(),
                        d1 = localDigits.getOrNull(1)?.toString(),
                        caret0 = if (caretSlot == 0) CaretPos.Center else CaretPos.None,
                        caret1 = if (caretSlot == 1) CaretPos.Center else CaretPos.None
                    )
                    Colon(active = len >= 2)
                    DigitPairBox(
                        d0 = localDigits.getOrNull(2)?.toString(),
                        d1 = localDigits.getOrNull(3)?.toString(),
                        caret0 = if (caretSlot == 2) CaretPos.Center else CaretPos.None,
                        caret1 = when {
                            caretAtEnd -> CaretPos.Right
                            caretSlot == 3 -> CaretPos.Center
                            else -> CaretPos.None
                        }
                    )
                }
            }

            // Transparent input — captures keystrokes only. Native caret hidden; we render our own.
            // Editing model: append-only / delete-from-end (positionally-aware backspace would
            // be confusing when the native caret is invisible).
            Input(type = InputType.Text) {
                value(localDigits)
                onInput { ev ->
                    val target = ev.target as? HTMLInputElement
                    val rawTyped = target?.value ?: ev.value
                    val digits = rawTyped.filter { it.isDigit() }.take(4)
                    val h = digits.take(2)
                    val m = digits.drop(2)
                    val hClamped = h.toIntOrNull()?.coerceAtMost(23)?.toString()?.padStart(h.length, '0') ?: h
                    val mClamped = m.toIntOrNull()?.coerceAtMost(59)?.toString()?.padStart(m.length, '0') ?: m
                    val final = hClamped + mClamped

                    localDigits = final
                    if (target != null) {
                        if (target.value != final) target.value = final
                        target.setSelectionRange(final.length, final.length)
                    }

                    onValueChange(
                        if (final.length == 4) "${final.take(2)}:${final.drop(2)}" else ""
                    )
                }
                onKeyDown { ev ->
                    if (ev.key == "Backspace" || ev.key == "Delete") {
                        ev.preventDefault()
                        if (localDigits.isNotEmpty()) {
                            val next = localDigits.dropLast(1)
                            localDigits = next
                            (ev.target as? HTMLInputElement)?.let { input ->
                                input.value = next
                                input.setSelectionRange(next.length, next.length)
                            }
                            onValueChange(if (next.length == 4) "${next.take(2)}:${next.drop(2)}" else "")
                        }
                    }
                }
                onClick {
                    (it.target as? HTMLInputElement)?.let { input ->
                        val len = input.value.length
                        input.setSelectionRange(len, len)
                    }
                }
                onFocusIn { ev ->
                    focused = true
                    (ev.target as? HTMLInputElement)?.let { input ->
                        val len = input.value.length
                        input.setSelectionRange(len, len)
                    }
                }
                onFocusOut { focused = false }
                attr("inputmode", "numeric")
                attr("maxlength", "4")
                attr("aria-label", "Time")
                style {
                    property("position", "absolute")
                    property("top", "0")
                    property("left", "0")
                    property("width", "100%")
                    property("height", "100%")
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("color", "transparent")
                    property("caret-color", "transparent")
                    property("font-size", s(18))
                    property("font-family", "inherit")
                    property("text-align", "center")
                    property("padding", "0")
                    property("box-sizing", "border-box")
                    property("cursor", "text")
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .sFontSize(12)
                    .color(AppColors.Error)
                    .sMarginTop(2)
            )
        }
    }
}

private enum class CaretPos { None, Center, Right }

/** Dark pair box (#112140, rounded 6, padding 4) holding two fixed-width digit slots. */
@Composable
private fun DigitPairBox(d0: String?, d1: String?, caret0: CaretPos, caret1: CaretPos) {
    Row(
        modifier = Modifier
            .sBorderRadius(6)
            .sPadding(4)
            .styleModifier { property("background", PAIR_BG) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        DigitSlot(char = d0, caret = caret0)
        DigitSlot(char = d1, caret = caret1)
    }
}

/** One digit slot. Fixed width so width never shifts between typed/placeholder values. */
@Composable
private fun DigitSlot(char: String?, caret: CaretPos) {
    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "relative")
                property("width", s(12))
                property("height", s(22))
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        SpanText(
            char ?: "0",
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(FontWeight.Medium)
                .styleModifier {
                    property("color", if (char != null) DIGIT_COLOR else DIGIT_FADED)
                    property("letter-spacing", "0.5px")
                    property("line-height", s(22))
                    property("font-variant-numeric", "tabular-nums")
                    property("text-align", "center")
                }
        )
        if (caret != CaretPos.None) {
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    when (caret) {
                        CaretPos.Center -> {
                            property("left", "50%")
                            property("transform", "translateX(-50%)")
                        }
                        CaretPos.Right -> {
                            property("right", "-1px")
                        }
                        CaretPos.None -> {}
                    }
                    property("top", "10%")
                    property("bottom", "10%")
                    property("width", "1.5px")
                    property("background", DIGIT_COLOR)
                    property("border-radius", "1px")
                }
            )
        }
    }
}

@Composable
private fun Colon(active: Boolean) {
    SpanText(
        ":",
        modifier = Modifier
            .sFontSize(18)
            .fontWeight(FontWeight.Medium)
            .styleModifier {
                property("color", if (active) DIGIT_COLOR else DIGIT_FADED)
                property("letter-spacing", "0.5px")
                property("line-height", s(22))
                property("font-variant-numeric", "tabular-nums")
            }
    )
}
